import type { ReactNode } from 'react'
import { useIsMobile } from './useIsMobile'
import { MobileTabBar, MobileTabRow } from './MobileTabs'
import { RailChipTabs } from './RailChipTabs'

export interface FamilyTabSpec<T extends string = string> {
  key: T
  label: string
  icon?: ReactNode
  activeIcon?: ReactNode
  unread?: boolean
  count?: number
  testId?: string
}

export interface FamilyTabsProps<T extends string = string> {
  items: Array<FamilyTabSpec<T>>
  activeTab: T
  onChange: (tab: T) => void
  ariaLabel: string
  className?: string
}

export const FamilyTabs = <T extends string>({
  items,
  activeTab,
  onChange,
  ariaLabel,
  className,
}: FamilyTabsProps<T>) => {
  const isMobile = useIsMobile()

  if (isMobile) {
    return (
      <MobileTabBar ariaLabel={ariaLabel} surface="family">
        <MobileTabRow
          items={items.map((tab) => ({
            key: tab.key,
            label: tab.label,
            icon: tab.icon ?? <span />,
            activeIcon: tab.activeIcon,
            unread: tab.unread,
            count: tab.count,
            testId: tab.testId,
          }))}
          activeKey={activeTab}
          onChange={onChange}
          ariaLabel={ariaLabel}
          className={className}
        />
      </MobileTabBar>
    )
  }

  return (
    <div className={['familyTabs', 'familyTabs--desktop', className].filter(Boolean).join(' ')}>
      <RailChipTabs
        items={items.map((tab) => ({
          key: tab.key,
          label: tab.label,
          icon: tab.icon,
          count: tab.count,
          testId: tab.testId,
        }))}
        value={activeTab}
        onChange={onChange}
        orientation="horizontal"
        ariaLabel={ariaLabel}
      />
    </div>
  )
}
